<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export type EditorSlashItem = {
    id: string;
    label: string;
    description: string;
    keywords: string[];
};

const props = withDefaults(defineProps<{
    open: boolean;
    items: EditorSlashItem[];
    selectedIndex?: number;
    left?: number;
    top?: number;
    emptyText?: string;
    title?: string;
    searchable?: boolean;
}>(), { selectedIndex: 0, left: 0, top: 0, emptyText: 'No matching blocks.', title: 'Insert a block', searchable: false });

const emit = defineEmits<{ select: [id: string]; close: [] }>();
const menu = ref<{ $el?: HTMLElement } | null>(null);
const search = ref('');
const filteredItems = computed(() => props.searchable && search.value.trim()
    ? props.items.filter(item => `${item.label} ${item.description} ${item.keywords.join(' ')}`.toLowerCase().includes(search.value.trim().toLowerCase()))
    : props.items);
watch(() => props.open, open => { if (open) search.value = ''; });
function onPointerDown(event: PointerEvent) {
    if (props.open && !menu.value?.$el?.contains(event.target as Node)) emit('close');
}
function onKeyDown(event: KeyboardEvent) {
    if (props.open && event.key === 'Escape') emit('close');
}
onMounted(() => { document.addEventListener('pointerdown', onPointerDown); document.addEventListener('keydown', onKeyDown); });
onBeforeUnmount(() => { document.removeEventListener('pointerdown', onPointerDown); document.removeEventListener('keydown', onKeyDown); });
</script>

<template>
    <Teleport to="body">
        <Card v-if="open" ref="menu" class="fixed z-[200] max-h-80 w-72 overflow-y-auto border-border/80 bg-popover p-1.5 shadow-lg" :style="{ left: `${left}px`, top: `${top}px` }" role="listbox" :aria-label="title">
            <p class="px-2 py-1.5 text-xs font-medium text-muted-foreground">{{ title }}</p>
            <Input v-if="searchable" v-model="search" aria-label="Search actions" placeholder="Search actions…" class="mb-1 h-8" @keydown.enter.prevent="filteredItems[0] && emit('select', filteredItems[0].id)" />
            <p v-if="!filteredItems.length" class="px-2 py-3 text-sm text-muted-foreground">{{ emptyText }}</p>
            <Button v-for="(item, index) in filteredItems" :key="item.id" type="button" variant="ghost" class="mb-0.5 h-auto w-full justify-start px-2 py-2 text-left" :class="index === selectedIndex ? 'bg-accent text-accent-foreground' : ''" role="option" :aria-selected="index === selectedIndex" @mousedown.prevent @click="emit('select', item.id)">
                <span class="min-w-0"><span class="block text-sm font-medium">{{ item.label }}</span><span class="block text-xs font-normal text-muted-foreground">{{ item.description }}</span></span>
            </Button>
        </Card>
    </Teleport>
</template>
